/** Pure OAuth 2.0 / PKCE helpers for the Guardius hosted-page sign-in flow.
 *  No React, no storage, no network — all unit-testable. SIWE itself happens
 *  on Guardius's /auth/authorize page; this module only builds the authorize
 *  URL, mints the PKCE verifier/challenge + state, decodes JWT claims for
 *  client-side gating, and matches permission strings.
 *
 *  Ported from penseur-trading's lib/oauth.ts (server contract verified there
 *  2026-06-07 against penseur-guardius's authorize.ts and the SDK's
 *  rbac-permissions.ts); only the platform id differs. */

import type { RuntimeConfig } from "./config";

/** The literal OAuth public-client id for the hosted Saga dashboard. */
export const PLATFORM_ID = "penseur-saga";

/** Client-side shape check for invite codes; mirrors Guardius's format. */
export const INVITE_CODE_PATTERN = /^gi_[0-9abcdefghjkmnpqrstvwxyz]{26}$/;

/** Typed subset of the Guardius access-token claims the dashboard reads.
 *  No signature verification is done client-side — the snapshot API's Guardius
 *  authorizer is the enforcement point; these claims only drive UI gating. */
export interface Claims {
  sub: string;
  wallet_address?: string;
  permissions?: string[];
  permissions_truncated?: boolean;
  permissions_version?: number;
  exp?: number;
}

/** base64url-encode raw bytes (RFC 4648 §5, no padding). */
function base64UrlEncode(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** base64url-decode to a UTF-8 string; throws on malformed input. */
function base64UrlDecode(input: string): string {
  const padded = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad = padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4));
  const bin = atob(padded + pad);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

/** Generate a PKCE verifier + S256 challenge. The verifier is a 43-char
 *  base64url string (32 random bytes → no padding); the challenge is
 *  base64url(SHA-256(verifier)). Guardius enforces S256 + the 43-char
 *  base64url challenge shape. */
export async function generatePkce(): Promise<{ verifier: string; challenge: string }> {
  const verifierBytes = new Uint8Array(32);
  crypto.getRandomValues(verifierBytes);
  const verifier = base64UrlEncode(verifierBytes);
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  const challenge = base64UrlEncode(new Uint8Array(digest));
  return { verifier, challenge };
}

/** Opaque CSRF state: 32 random bytes → 43-char base64url. Guardius's
 *  /auth/authorize rejects a state shorter than 32 chars with WEAK_STATE. */
export function randomState(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** Decode the payload segment of a JWT into typed claims; `null` on anything
 *  malformed. NO signature verification client-side — UI gating only; the
 *  snapshot API's Guardius authorizer is the authoritative enforcement point. */
export function decodeJwtClaims(token: string): Claims | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const json = base64UrlDecode(parts[1]);
    const claims = JSON.parse(json) as unknown;
    if (typeof claims !== "object" || claims === null) return null;
    const c = claims as Record<string, unknown>;
    if (typeof c.sub !== "string") return null;
    return c as unknown as Claims;
  } catch {
    return null;
  }
}

/** Segment-wise match over the 6-segment
 *  `region:platform:tenantId:resourceType:resourceId:action` permission format.
 *
 *  Guardius's server and SDK use SYMMETRIC wildcard matching — a `*` in EITHER
 *  the granted segment OR the required segment matches that position. Both
 *  sides return false for any string that isn't exactly 6 segments (never
 *  throw). This implementation matches that semantics exactly so client-side
 *  gating agrees with the server's authorizer. */
export function permissionMatches(granted: string[] | undefined, required: string): boolean {
  if (!granted) return false;
  const c = required.split(":");
  if (c.length !== 6) return false;
  return granted.some((grant) => {
    const g = grant.split(":");
    if (g.length !== 6) return false;
    for (let i = 0; i < 6; i++) {
      if (g[i] !== "*" && c[i] !== "*" && g[i] !== c[i]) return false;
    }
    return true;
  });
}

/** Build the Guardius hosted-page authorize URL with the exact query params
 *  the server expects. `platform_id` is the literal dashboard client id;
 *  `tenant_id` is the saga tenant UUID from runtime config. */
export function buildAuthorizeUrl(
  config: RuntimeConfig,
  args: { challenge: string; state: string; redirectUri: string; invite?: string },
): string {
  const url = new URL("/auth/authorize", config.guardiusUrl);
  const params = new URLSearchParams({
    response_type: "code",
    redirect_uri: args.redirectUri,
    code_challenge: args.challenge,
    code_challenge_method: "S256",
    state: args.state,
    platform_id: PLATFORM_ID,
    tenant_id: config.tenantId,
  });
  // Optional invite code. Guardius (v2.23+) validates it fail-fast pre-auth on
  // /auth/authorize and redeems it after sign-in but before minting the code, so
  // the dashboard only needs to thread the param through (URLSearchParams handles
  // URL-encoding). Absent when the user reaches sign-in without an invite link.
  if (args.invite) params.set("invite", args.invite);
  url.search = params.toString();
  return url.toString();
}
